import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: 19,
        color: TEXT,
        margin: "28px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontSize: 16, lineHeight: 1.75, color: TEXT_DIM, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 10 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}44` }}
      {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        background: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: 4,
        fontFamily: "var(--mono)",
        fontSize: 13,
        color: TEXT,
      }}
    >
      {children}
    </code>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        background: "rgba(79,205,196,0.05)",
        borderRadius: "0 12px 12px 0",
        fontFamily: "var(--display)",
        fontSize: 19,
        lineHeight: 1.5,
        color: TEXT,
        fontWeight: 500,
      }}
    >
      {children}
    </blockquote>
  );
}

function Callout({ kind = "info", title, children }) {
  const palette =
    kind === "warn"
      ? { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)", titleColor: "#f87171" }
      : { bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.25)", titleColor: "#a78bfa" };
  return (
    <div
      style={{
        margin: "24px 0",
        padding: "18px 22px",
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          fontFamily: "var(--display)",
          fontWeight: 600,
          fontSize: 15,
          color: palette.titleColor,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM }}>{children}</div>
    </div>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "36px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: "rgba(79,205,196,0.06)",
        border: "1px solid rgba(79,205,196,0.2)",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 14 }}>{children}</div>
      <a
        href={href}
        style={{
          display: "inline-block",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          minHeight: 44,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

// ── FAQs ──
const faqs = [
  {
    q: "What is automatic advanced matching in ChatGPT Ads?",
    a: "Automatic advanced matching is a Measurement Pixel feature that improves conversion measurement when a click identifier is unavailable. When enabled, OpenAI&#39;s pixel automatically detects supported customer information entered into website forms, normalizes it, and hashes it in the browser with SHA-256 before including it with conversion events. OpenAI&#39;s documentation states that raw customer information is not sent through automatic advanced matching, and that no changes to your pixel implementation are needed.",
  },
  {
    q: "What changes for ChatGPT Ads pixels on August 17, 2026?",
    a: "Two things, per OpenAI&#39;s dated API changelog. First, new Web pixels created through the Ads API get automatic advanced matching enabled whenever the automatic_advanced_matching_enabled field is omitted; before August 17 that field defaults to false. Second, OpenAI enables automatic advanced matching for all existing Web pixels created through the Ads API, unless it was explicitly disabled or the ad account opted out. The change is retroactive: doing nothing means existing API-created pixels flip on.",
  },
  {
    q: "How do I turn off automatic advanced matching for a ChatGPT Ads pixel?",
    a: "Pass automatic_advanced_matching_enabled: false explicitly when creating or updating the pixel through the Ads API. After August 17, omitting the field on a new API-created web pixel means enabled, so an explicit false is the only reliable off switch at the pixel level. OpenAI&#39;s changelog also references an ad-account-level opt-out; no public API surface for it is documented, so account-wide opt-outs run through OpenAI directly.",
  },
  {
    q: "Does the ChatGPT Ads pixel respect consent by default?",
    a: "The pixel has a consent feature, but you must wire it. OpenAI&#39;s documentation states the pixel initializes consent to true by default unless you set it to false or it finds a stored denial. In practice that means a site whose consent tool never calls oaiq(&#39;consent&#39;, false) runs the pixel as fully consented. When consent is denied, the pixel stops sending measurement events, and events blocked during denial are not replayed after consent is granted.",
  },
  {
    q: "Does automatic advanced matching send raw email addresses to OpenAI?",
    a: "No, per OpenAI&#39;s documentation. The pixel normalizes and hashes detected customer information in the browser using SHA-256 before it is included with conversion events, and raw customer information is not sent to OpenAI through automatic advanced matching. Hashing in the browser is the same pattern Meta and Google use for advanced matching features. Whether hashed identifiers are acceptable under your consent posture is a question for your privacy team, not a technical setting.",
  },
  {
    q: "Does the August 17 change affect the Conversions API or the image tag?",
    a: "No. OpenAI&#39;s API reference states automatic advanced matching is available only for web pixels, and the August 17 changelog entry covers Web pixels created through the Ads API. Server-side Conversions API events and the no-JavaScript image tag only carry the customer information you explicitly pass them, so nothing about their behavior changes automatically.",
  },
];

function Content() {
  return (
    <article>
      {/* ── Featured-snippet box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            On August 17, 2026, OpenAI turns automatic advanced matching on by default for ChatGPT Ads web pixels created through the Ads API, retroactively including existing ones unless they were explicitly disabled or the ad account opted out. When enabled, the pixel detects customer information in website forms and sends an SHA-256 hash of it with conversion events. If you run ChatGPT Ads, you have until Sunday to decide whether that is what you want.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 0" }}>
          <Li><Strong>What flips:</Strong> <Code>automatic_advanced_matching_enabled</Code> goes from default <Code>false</Code> to default enabled for API-created web pixels</Li>
          <Li><Strong>Retroactive:</Strong> yes, for existing API-created web pixels, unless explicitly disabled or account opted out</Li>
          <Li><Strong>What it collects:</Strong> supported customer information from website forms, hashed in-browser with SHA-256 before sending</Li>
          <Li><Strong>Off switch:</Strong> pass <Code>false</Code> explicitly per pixel via the Ads API</Li>
          <Li><Strong>Consent default:</Strong> the pixel initializes consent to true unless your consent tool explicitly denies it</Li>
          <Li><Strong>Scope:</Strong> web pixels only; the Conversions API and image tag are unaffected</Li>
        </ul>
      </div>

      {/* ── Intro ── */}
      <P>
        OpenAI pre-announced this one in its own <A href="https://developers.openai.com/ads/api-overview">Ads API changelog</A>, dated August 17, 2026: new web pixels created through the Ads API will have automatic advanced matching enabled whenever the field is omitted, and the setting will be switched on for all existing API-created web pixels unless it was explicitly disabled or the ad account opted out. Trade coverage has reported the date. What has been missing is a technical account of what the feature actually does, what the pixel&#39;s consent machinery actually does, and what to change before Sunday. That is this post.
      </P>
      <P>
        Everything below cites either OpenAI&#39;s developer documentation or the shipped pixel SDK itself, which we downloaded and read for this post. Where a claim comes from the SDK rather than the docs, we say so and pin the version.
      </P>

      <PullQuote>
        A default is a decision made on your behalf. August 17 changes whose decision it is.
      </PullQuote>

      {/* ── What is AAM ── */}
      <H2 id="what-is-automatic-advanced-matching">What is automatic advanced matching?</H2>
      <P>
        Automatic advanced matching exists to recover conversions that attribution would otherwise lose. OpenAI&#39;s <A href="https://developers.openai.com/ads/measurement-pixel">Measurement Pixel documentation</A> describes it as improving website conversion measurement by helping match more conversions to your ads <Strong>when a click identifier is unavailable</Strong>. The click identifier in question is <Code>oppref</Code>, the parameter ChatGPT appends to ad clicks; when cookie loss, redirects, or cross-device journeys break that chain, matching falls back to who the user is rather than which click they came from.
      </P>
      <P>
        Mechanically, per the same documentation: when enabled, the pixel automatically detects supported customer information entered into website forms, normalizes it, and hashes it in the browser using SHA-256 before including it with conversion events. Raw customer information is not sent to OpenAI through automatic advanced matching, and no changes to your pixel implementation are needed. This is the same design Meta and Google use for their advanced matching features: the identifier leaves the page as a hash, and matching happens against hashes on the platform side.
      </P>
      <P>
        Two boundaries worth stating precisely. It is a <Strong>web pixel</Strong> feature only: OpenAI&#39;s API reference states it is not available for other client types, so <A href="/blog/chatgpt-ads-conversion-tracking">Conversions API</A> events and the no-JavaScript image tag carry only what you explicitly pass them. And it is a matching feature, not a new event stream: it adds hashed identifiers to the conversion events your pixel already sends.
      </P>

      {/* ── What changes ── */}
      <H2 id="what-changes-august-17">What exactly changes on August 17</H2>
      <P>
        The <A href="https://developers.openai.com/ads/api-reference/conversion-setup">conversion setup API reference</A> carries the change as a note on the pixel-creation endpoint, and it is two changes, not one:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>
          <Strong>New pixels:</Strong> &quot;Beginning August 17, 2026, new Web pixels created through the Ads API have automatic advanced matching enabled when this field is omitted. Pass <Code>false</Code> explicitly to disable it. Before August 17, the field defaults to <Code>false</Code>.&quot;
        </Li>
        <Li>
          <Strong>Existing pixels:</Strong> &quot;Also on August 17, OpenAI will enable automatic advanced matching for all existing Web pixels created through the Ads API, unless it was explicitly disabled or the ad account opted out.&quot;
        </Li>
      </ul>
      <P>
        The second bullet is the one that deserves your attention. This is not a change of default for future decisions; it is a retroactive flip of a setting on infrastructure you already run. If your pixel was created through the Ads API and nobody ever explicitly set <Code>automatic_advanced_matching_enabled</Code> to <Code>false</Code>, it turns on Sunday without any action from you.
      </P>
      <P>
        One scope note, stated as precisely as the source allows: both changelog bullets cover pixels <Strong>created through the Ads API</Strong>. OpenAI&#39;s announcement does not state what happens to pixels provisioned through other surfaces, so if yours was created in the Ads Manager UI, check its setting there rather than assuming either way.
      </P>

      {/* ── Consent mechanics ── */}
      <H2 id="how-pixel-consent-actually-works">How the pixel&#39;s consent machinery actually works</H2>
      <P>
        The August 17 change lands differently depending on your consent setup, and this is where most coverage stops at the press release. The pixel&#39;s documented consent behavior is the place to start: OpenAI&#39;s docs state that <Strong>the pixel initializes consent to true by default</Strong> unless you set it to <Code>false</Code> or the pixel finds a stored denial. Consent is opt-out at the SDK level. A site that installs the pixel and never wires its consent banner to it runs fully consented, and after Sunday that includes hashed form-field identifiers on conversion events.
      </P>
      <P>
        For this post we also downloaded and read the shipped SDK itself (<Code>oaiq.min.js</Code> v0.1.20, fetched August 12, 2026, SHA-256 <Code>7a6274b5…2720</Code>), so the following is verified against the exact bytes your visitors&#39; browsers execute, as of that version. The consent gate in the shipped code treats only an explicit <Code>false</Code> as denial. When denial is set, the SDK drops measurement events before they are queued, and its own debug log says so plainly: &quot;event dropped because consent is not granted&quot;. It purges any queued measurement events, and the denial handler clears the stored click identifier and browser reference. One class of traffic still flows under denial: anonymous diagnostic lifecycle pings, which the code sends with identifiers stripped and browser credentials omitted.
      </P>
      <P>
        Read as a whole, that is a reasonable consent implementation with one sharp edge: <Strong>everything hinges on your consent tool actually calling it</Strong>. Denial genuinely stops measurement. But silence is treated as consent, and the burden of saying no sits with your integration, per OpenAI&#39;s own wiring pattern:
      </P>
      <CodeBlock lang="Consent wiring" syntax="javascript">{`// Set consent BEFORE initializing the pixel.
oaiq("consent", false);
oaiq("init", {
  pixelId: "<YOUR-PIXEL-ID>",
});

// Call this after the user grants measurement consent.
oaiq("consent", true);
// Note: events blocked while consent was denied are not replayed.`}</CodeBlock>
      <P>
        If you advertise in the UK or the EU launch markets, the practical takeaway is not legal advice, it is a checklist item: confirm your consent platform calls <Code>oaiq(&quot;consent&quot;, false)</Code> for users who decline, before the pixel initializes, and review the advanced matching decision with your privacy team while the default still belongs to you. Attribution context lives alongside your <A href="/glossary/utm-parameters">UTM parameters</A> and the <Code>oppref</Code> click identifier either way; advanced matching only changes what happens when those are missing.
      </P>

      {/* ── What to do ── */}
      <H2 id="what-to-do-before-august-17">What to do before August 17</H2>
      <H3>1. Inventory your pixels</H3>
      <P>
        List the web pixels on your ad account through the Ads API and check each one&#39;s <Code>automatic_advanced_matching_enabled</Code> value. Anything created through the API that was never explicitly disabled will flip on Sunday.
      </P>
      <H3>2. Decide, and make the decision explicit</H3>
      <P>
        If you want advanced matching, do nothing on the pixel and spend the time on the consent check below instead. If you do not, pass an explicit <Code>false</Code>, because after August 17 omission means enabled on new API-created pixels:
      </P>
      <CodeBlock lang="Disable via the Ads API" syntax="bash">{`curl -X POST https://api.ads.openai.com/v1/conversion_sources \\
  -H "Authorization: Bearer $ADS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Acme website",
    "client_type": "web",
    "automatic_advanced_matching_enabled": false
  }'`}</CodeBlock>
      <P>
        The changelog also references an ad-account-level opt-out. No public API surface for it is documented, so treat account-wide opt-outs as a conversation with OpenAI rather than a field you can set.
      </P>
      <H3>3. Verify your consent wiring, which matters more than the toggle</H3>
      <P>
        Load your site in a clean browser, decline consent in your banner, and confirm in the console (with <Code>debug: true</Code>) that the pixel logs dropped events rather than sending them. That single test verifies the entire chain that the August 17 change makes consequential.
      </P>
      <H3>4. Re-check after the flip</H3>
      <P>
        On or after August 17, re-read the field on your existing API-created pixels and confirm the state matches your decision. Retroactive changes are exactly the kind that quiet audits catch and assumptions miss.
      </P>

      <Callout kind="warn" title="Doing nothing is a choice now">
        Before August 17, an omitted field meant off. After it, omission means on for new API-created pixels, and existing API-created pixels flip unless explicitly disabled or the account opted out. Whatever you decide, make it explicit in the API so the default never decides for you again.
      </Callout>

      <InlineCTA href="/integrations/chatgpt-ads" label="Connect the ChatGPT Ads integration">
        The AI-Advisors ChatGPT Ads integration stages your pixel install in Google Tag Manager: paste your Pixel ID, pick your container, publish in GTM. Available on Growth+ plans.
      </InlineCTA>

      {/* ── FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q.replace(/&#39;/g, "'"),
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a.replace(/&#39;/g, "'"),
              },
            })),
          }),
        }}
      />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">How to install the ChatGPT Ads Measurement Pixel via Google Tag Manager</A></Li>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/how-to-add-utm-codes-to-chatgpt-ads">How to Add UTM Codes to ChatGPT Ads</A></Li>
        <Li><A href="/blog/how-to-use-chatgpt-ads-manager">How to Use ChatGPT Ads: A Marketer&#39;s Step-by-Step Guide (2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "chatgpt-ads-automatic-advanced-matching",
  title: "ChatGPT Ads Automatic Advanced Matching Turns On August 17",
  seoTitle: "ChatGPT Ads Automatic Advanced Matching Turns On Aug 17",
  excerpt:
    "On August 17, 2026, OpenAI enables automatic advanced matching by default for ChatGPT Ads web pixels created through the Ads API, retroactively. What the feature collects, how the pixel's consent machinery actually behaves in the shipped SDK, and how to make the decision explicit before the default makes it for you.",
  metaDescription:
    "OpenAI enables automatic advanced matching for API-created ChatGPT Ads pixels on August 17, 2026. What it collects, how pixel consent works, how to opt out.",
  date: "2026-08-12",
  readTime: "9 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "Advanced Matching", "Measurement Pixel", "Consent", "Conversion Tracking", "AI Advertising"],
  Content,
};

export default post;
